import {
  LoginInput,
  LoginOutput,
  LoginWithGoogleInput,
  LogOutInput,
  LogOutOutput,
  ResetPasswordInput,
  ResetPasswordOutput,
  SubscribeOutput,
  ThirdPartLoginInput,
} from '@allocab/allocab-commons-shared-types';
import { PassengerSubscribeInput } from '@allocab/allocab-passenger-shared-types';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { useTokens } from './useTokens';
import { passengerApi } from '../services/network/Api';
import { APIError } from '../services/network/types';
import { noop } from '../utils/common';

const signin = (input: LoginInput) =>
  passengerApi.post<LoginInput, LoginOutput>('/authentication/v1/user/login', input, {
    skipAuth: true,
  });

const facebookSignin = (input: ThirdPartLoginInput) =>
  passengerApi.post<ThirdPartLoginInput, LoginOutput>('/authentication/v1/user/login/facebook', input);

const googleSignin = (input: LoginWithGoogleInput) =>
  passengerApi.post<LoginWithGoogleInput, LoginOutput>('/authentication/v1/user/login/google', input);

const logout = async (accessToken?: string) => {
  if (!accessToken) return null;

  return passengerApi.post<LogOutInput, LogOutOutput>(
    '/authentication/v1/user/logout',
    { accessToken, pushToken: '' },
    { skipAuth: true },
  );
};

const signup = (input: PassengerSubscribeInput) =>
  passengerApi.post<PassengerSubscribeInput, SubscribeOutput>('/authentication/v1/user/subscribe', input, {
    skipAuth: true,
  });

const resetPassword = async (input: ResetPasswordInput) =>
  passengerApi.post<ResetPasswordInput, ResetPasswordOutput>('/authentication/v1/user/reset-password', input, {
    skipAuth: true,
  });

export const useAuth = () => {
  const { tokens, updateTokens, clearTokens } = useTokens();
  const queryClient = useQueryClient();

  const handleSuccessfulSignin = (tokens: LoginOutput) => {
    updateTokens(queryClient, tokens);
  };

  const handleSuccessfulSignup = (tokens: SubscribeOutput) => {
    updateTokens(queryClient, tokens);
  };

  const signinMutation = useMutation<LoginOutput, unknown, LoginInput>({
    mutationFn: signin,
    onSuccess: tokens => handleSuccessfulSignin(tokens),
  });

  const googleSigninMutation = useMutation<LoginOutput, unknown, LoginWithGoogleInput>({
    mutationFn: googleSignin,
    onSuccess: tokens => handleSuccessfulSignin(tokens),
    onError: () => noop(),
  });

  const facebookSigninMutation = useMutation<LoginOutput, APIError, ThirdPartLoginInput>({
    mutationFn: facebookSignin,
    onSuccess: tokens => handleSuccessfulSignin(tokens),
  });

  const signupMutation = useMutation<SubscribeOutput, unknown, PassengerSubscribeInput>({
    mutationFn: signup,
    onSuccess: tokens => handleSuccessfulSignup(tokens),
  });

  const resetPasswordMutation = useMutation<ResetPasswordOutput, unknown, ResetPasswordInput>({
    mutationFn: resetPassword,
  });

  const logoutMutation = useMutation({
    mutationFn: () => logout(tokens?.accessToken),
    onMutate: () => {
      clearTokens(queryClient);
    },
    onError: () => noop(),
  });

  return {
    signinMutation,
    googleSigninMutation,
    facebookSigninMutation,
    signupMutation,
    logoutMutation,
    resetPasswordMutation,
  };
};
